import { Preferences } from '@capacitor/preferences';
import { PayloadAction } from '@reduxjs/toolkit';
import Keys from 'preferences/preferences';
import resetAll from 'store/shared/resetAll';
import createSlice from 'utils/@reduxjs/toolkit';
import { ConversationState } from './types';

const saveDraftsToStorage = (drafts: { [conversationId: string]: string | undefined }) => {
  Preferences.set({ key: Keys.MESSAGE_DRAFTS, value: JSON.stringify(drafts) }).catch(error => {
    console.error('Failed to save message drafts to storage:', error);
  });
};

const loadDraftsFromStorage = async (): Promise<{ [conversationId: string]: string | undefined }> => {
  try {
    const result = await Preferences.get({ key: Keys.MESSAGE_DRAFTS });
    if (!result.value) return {};
    return JSON.parse(result.value);
  } catch (error) {
    console.error('Failed to load message drafts from storage:', error);
    return {};
  }
};

export const initialState: ConversationState = {
  websocketConnected: false,
  summaryPerConversation: {},
  summaryDraftPerConversation: {},
  conversationInputTexts: {},
  replyingToMessage: {},
};

/**
 * Redux slice for conversation-adjacent UI state.
 *
 * Messages now live in TanStack Query under `conversationMessagesQueryKey(conversationId)`
 * (see hooks/useConversations.tsx). What's kept here:
 *
 * - drafts (`conversationInputTexts`) and reply targets (`replyingToMessage`) — UI
 *   state per conversation.
 * - summaries (`summaryPerConversation`, `summaryDraftPerConversation`) — caregiver
 *   notes per conversation.
 * - `websocketConnected` — used as a signal to gap-recover queries.
 */
const slice = createSlice({
  name: 'conversations',
  initialState,
  extraReducers: (builder: any) => builder.addCase(resetAll, () => initialState),
  reducers: {
    loadPersistedDrafts: (state, action: PayloadAction<{ [conversationId: string]: string | undefined }>) => {
      state.conversationInputTexts = action.payload;
    },
    setWebsocketConnectionStatus: (state, action: PayloadAction<{ connected: boolean }>) => {
      state.websocketConnected = action.payload.connected;
    },
    setConversationSummary: (state, action: PayloadAction<{ conversationId: string; summary: string }>) => {
      state.summaryPerConversation[action.payload.conversationId] = action.payload.summary;
    },
    setConversationSummaryDraft: (state, action: PayloadAction<{ conversationId: string; summary: string }>) => {
      state.summaryDraftPerConversation[action.payload.conversationId] = action.payload.summary;
    },
    updateConversationInputText: (
      state,
      action: PayloadAction<{
        conversationId: string;
        inputText?: string;
      }>,
    ) => {
      const { conversationId, inputText } = action.payload;

      state.conversationInputTexts = {
        ...state.conversationInputTexts,
        [conversationId]: inputText,
      };
      saveDraftsToStorage(state.conversationInputTexts);
    },
    setReplyingToMessage: (
      state,
      action: PayloadAction<{
        conversationId: string;
        replyingTo?: { messageIndex: number; messageId: number; messageText: string; senderName: string };
      }>,
    ) => {
      const { conversationId, replyingTo } = action.payload;
      state.replyingToMessage = {
        ...state.replyingToMessage,
        [conversationId]: replyingTo,
      };
    },
  },
});

/**
 * `actions` will be used to trigger change in the state from where ever you want
 */
export const { actions: conversationActions, reducer: conversationReducer } = slice;

export { loadDraftsFromStorage };
